/* eslint-disable @typescript-eslint/restrict-plus-operands */
import { Box, Button, Grid } from '@mui/material'
import { sendGet, sendPost } from 'hooks/use-fetch'
import { useEffect, useState } from 'react'
import { DatePickerWithLabel } from 'Components/DatePicker'
import { SelectWithLabel, type SelectOption } from 'Components/SelectWithLabel'
import { useAccountId } from 'ContextProviders/CurrentAccount'
import ChipSelect from 'Components/ChipSelect'
import { toast } from 'react-toastify'
import { AccountType, type ActiveAccount, NewEnrollmentStatus } from 'core/types'
import { type EnrollmentDetailsReportModel } from './EnrollmentDetailsReport.types'
import { EnrollmentDetailsReportTableView } from './EnrollmentDetailsReportTableView'
import { ReportHeader } from 'Pages/Reports/ReportHeader'

export function EnrollmentDetailsReport (): JSX.Element {
  const [loading, setLoading] = useState(false)
  const [reportData, setReportData] = useState<EnrollmentDetailsReportModel[]>([])
  const [showResults, setShowResults] = useState(false)
  const [actionStartDate, setActionStartDate] = useState<Date | null>(null)
  const [actionEndDate, setActionEndDate] = useState<Date | null>(null)

  const [programs, setPrograms] = useState<Array<SelectOption<number>>>([])
  const [selectedPrograms, setSelectedPrograms] = useState<Array<SelectOption<number>>>([])
  const [serviceProviders, setServiceProviders] = useState<Array<SelectOption<number>>>([])
  const [selectedServiceProviders, setSelectedServiceProviders] = useState<Array<SelectOption<number>>>([])
  const [districts, setDistricts] = useState<Array<SelectOption<number>>>([])
  const [districtID, setSelectedDistrictID] = useState<number | undefined>(undefined)
  const [schoolYears, setSchoolYears] = useState<Array<SelectOption<number>>>([])
  const [schoolYearID, setSelectedSchoolYearID] = useState<number | undefined>(undefined)

  const statuses: Array<SelectOption<string>> = [
    { id: 'All', name: 'All' },
    { id: NewEnrollmentStatus.Referred.toString(), name: 'Referred' },
    { id: NewEnrollmentStatus.Processing.toString(), name: 'Processing' },
    { id: NewEnrollmentStatus.Declined.toString(), name: 'Declined' },
    { id: NewEnrollmentStatus.Waitlisted.toString(), name: 'Waitlisted' },
    { id: NewEnrollmentStatus.Enrolled.toString(), name: 'Enrolled' },
    { id: NewEnrollmentStatus.Started.toString(), name: 'Started' },
    { id: NewEnrollmentStatus.Exited.toString(), name: 'Exited' }
  ]
  const [selectedStatuses, setSelectedStatuses] = useState<Array<SelectOption<string>>>([])

  const account: ActiveAccount | undefined = useAccountId()

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const { response } = await sendGet('/Reporting/ISDPrograms', {})
      setPrograms(response)
      const { response: r2 } = await sendGet('/Reporting/ServiceProviders', {})
      setServiceProviders(r2)
      const { response: r3 } = await sendGet('/Reporting/ResidentDistricts', {})
      setDistricts(r3)
      const { response: r4 } = await sendGet('/EnrollmentSearch/GetSchoolYears', {})
      setSchoolYears(r4)
    }

    void fetchData()
  }, [])

  const generateReport = async (): Promise<void> => {
    if (actionStartDate === null && actionEndDate === null) {
      toast.error('Please enter at least one date range filter.')
      return
    }

    if ((actionStartDate === null) !== (actionEndDate === null)) {
      toast.error('Please enter both values for the Enrollment date range.')
      return
    }

    if (selectedStatuses.length === 0) {
      toast.error('Please select at least one enrollment status.')
      return
    }

    const isdServiceProviderIDs = selectedServiceProviders.map(s => s.id)
    const ISDProgramIDs = selectedPrograms.map(s => s.id)
    const statusList = selectedStatuses.map(s => s.id)

    const options = {
      statuses: statusList.filter(e => e !== 'All'),
      isdid: account?.type === AccountType.ISD ? account?.id : account?.currentISDID,
      actionStart: actionStartDate,
      actionEnd: actionEndDate,
      schoolYearID,
      districtID,
      serviceProviderIDs: isdServiceProviderIDs,
      ISDProgramIDs
    }

    setLoading(true)
    try {
      const { response, success } = await sendPost('/Report/EnrollmentDetailsReport/GetEnrollmentDetailsReport', options)
      if (success) {
        setReportData(response)
        setShowResults(true)
      } else {
        toast.error('Failed to generate report.')
      }
    } catch (error) {
      toast.error('An error occurred while generating the report.')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <ReportHeader title="Enrollment Details Report" />
      <Grid container columns={7} gap={1} sx={{ display: 'flex', flexDirection: 'row' }}>
        <Grid item xs={2}>
          <DatePickerWithLabel
            label='Enrollment Date, Begin Range'
            name='actionStartDate'
            onChange={setActionStartDate}
            value={actionStartDate}
          />
        </Grid>
        <Grid item xs={2}>
          <DatePickerWithLabel
            label='Enrollment Date, End Range'
            name='actionEndDate'
            onChange={setActionEndDate}
            value={actionEndDate}
          />
        </Grid>
        <Grid item xs={2}>
          <SelectWithLabel
            includeNoneOption={true}
            noneOptionText='(All)'
            name="schoolYearID"
            label="School Year"
            value={schoolYearID}
            width="100%"
            options={schoolYears}
            onChange={(e) => { setSelectedSchoolYearID(e.target.value === '' ? undefined : Number(e.target.value)) }}
          />
        </Grid>
        <Grid item xs={2}>
          <SelectWithLabel
            includeNoneOption={true}
            noneOptionText='(All)'
            name="districtID"
            label="Resident District"
            value={districtID}
            width="100%"
            options={districts}
            onChange={(e) => { setSelectedDistrictID(Number(e.target.value)) }}
          />
        </Grid>
        <Grid item xs={2}>
          <ChipSelect
            label='Enrollment Status'
            options={statuses}
            values={selectedStatuses.map(s => s.id)}
            onChange={(newVals) => {
              if (newVals.some(e => e === 'All') && newVals.length >= selectedStatuses.length) {
                setSelectedStatuses([...statuses])
              } else if (newVals.some(e => e === 'All') && newVals.length < selectedStatuses.length) {
                const newSelectedValues = [...statuses].filter(o => newVals.some(v => v === o.id && o.id !== 'All'))
                setSelectedStatuses(newSelectedValues)
              } else if (selectedStatuses.some(e => e.id === 'All') && !newVals.some(e => e === 'All')) {
                setSelectedStatuses([])
              } else if (!newVals.some(e => e === 'All') && (newVals.length + 1) === statuses.length) {
                setSelectedStatuses([...statuses])
              } else {
                const newSelectedValues = [...statuses].filter(o => newVals.some(v => v === o.id))
                setSelectedStatuses(newSelectedValues)
              }
            }}
            name={'enrollmentStatuses'}
          />
        </Grid>
        <Grid item xs={2}>
          <ChipSelect
            label='Programs'
            options={programs.map(p => ({ ...p, id: p.id.toString() }))}
            values={selectedPrograms.map(s => s.id.toString())}
            onChange={(newVals) => {
              const newSelectedValues = [...programs].filter(o => newVals.some(v => v === o.id.toString()))
              setSelectedPrograms(newSelectedValues)
            }}
            name={'programs'}
          />
        </Grid>

        {account !== undefined && account.type !== AccountType.SERVICE_PROVIDER && (
          <Grid item xs={2}>
            <ChipSelect
              label='Service Providers'
              options={serviceProviders.map(p => ({ ...p, id: p.id.toString() }))}
              values={selectedServiceProviders.map(s => s.id.toString())}
              onChange={(newVals) => {
                const newSelectedValues = [...serviceProviders].filter(o => newVals.some(v => v === o.id.toString()))
                setSelectedServiceProviders(newSelectedValues)
              }}
              name={'serviceProviders'}
            />
          </Grid>
        )}
      </Grid>

      <Box className='mt-10'>
        <Button
          color='secondary'
          variant='contained'
          onClick={generateReport}
          disabled={loading}
        >
          {loading ? 'Generating...' : 'Generate Report'}
        </Button>
      </Box>
      {showResults &&
        <Box>
          <EnrollmentDetailsReportTableView reportData={reportData} loading={loading} />
        </Box>
      }
    </>
  )
}
